import QRCodeStyling from "qr-code-styling";
import { Check, Copy, Share2 } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const DOT_TYPE_OPTIONS = [
  "rounded",
  "dots",
  "classy",
  "classy-rounded",
  "square",
  "extra-rounded",
];

const CORNER_SQUARE_OPTIONS = ["dot", "square", "extra-rounded"];

const QRCodeCard = ({ value, fileName = "qr-code", size = 220, image }) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const qrCodeRef = useRef(null);

  const [dotsColor, setDotsColor] = useState("#000000");
  const [cornerColor, setCornerColor] = useState("#000000");
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [dotsType, setDotsType] = useState("rounded");
  const [cornersSquareType, setCornersSquareType] = useState("extra-rounded");
  const [isCopied, setIsCopied] = useState(false);

  const options = useMemo(
    () => ({
      width: size,
      height: size,
      type: "svg",
      data: value || "",
      margin: 8,
      dotsOptions: { color: dotsColor, type: dotsType },
      backgroundOptions: { color: backgroundColor },
      cornersSquareOptions: { color: cornerColor, type: cornersSquareType },
      cornersDotOptions: { color: cornerColor, type: cornersSquareType },
      image,
      imageOptions: {
        crossOrigin: "anonymous",
        hideBackgroundDots: true,
        imageSize: 0.45,
        margin: 4,
      },
    }),
    [
      value,
      size,
      image,
      dotsColor,
      cornerColor,
      backgroundColor,
      dotsType,
      cornersSquareType,
    ],
  );

  useEffect(() => {
    if (!containerRef.current) return;
    if (!qrCodeRef.current) {
      qrCodeRef.current = new QRCodeStyling(options);
      qrCodeRef.current.append(containerRef.current);
    } else {
      qrCodeRef.current.update(options);
    }
  }, [options]);

  if (!value) return null;

  const handleDownload = (extension) => {
    qrCodeRef.current?.download({ name: fileName, extension });
  };

  const copyLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = value;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setIsCopied(true);
      window.setTimeout(() => setIsCopied(false), 1800);
    } catch {
      setIsCopied(false);
    }
  };

  const shareLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: fileName, url: value });
        return;
      }
      await copyLink();
    } catch {
      // Closing the native share dialog is not an error for the user.
    }
  };

  return (
    <div className="qr_code_card">
      <div className="qr_code_preview" ref={containerRef} />

      <div className="qr_code_link">
        <label htmlFor="qr-code-link">{t("QR Link")}</label>
        <div className="qr_code_link_actions">
          <input id="qr-code-link" type="text" value={value} readOnly />
          <button
            type="button"
            className="icon_btn"
            onClick={copyLink}
            title={isCopied ? t("Copied") : t("Copy link")}
            aria-label={isCopied ? t("Copied") : t("Copy link")}
          >
            {isCopied ? <Check size={18} /> : <Copy size={18} />}
          </button>
          <button
            type="button"
            className="icon_btn"
            onClick={shareLink}
            title={t("Share link")}
            aria-label={t("Share link")}
          >
            <Share2 size={18} />
          </button>
        </div>
      </div>

      <div className="d-flex flex-column w-100 gap-3">
        <div className="qr_code_controls">
          <div className="qr_code_control_row">
            <label>{t("Dot Color")}</label>
            <div className="qr_color_picker">
              <input
                type="color"
                value={dotsColor}
                onChange={(e) => setDotsColor(e.target.value)}
              />
            </div>
          </div>

          <div className="qr_code_control_row">
            <label>{t("Background Color")}</label>
            <div className="qr_color_picker">
              <input
                type="color"
                value={backgroundColor}
                onChange={(e) => setBackgroundColor(e.target.value)}
              />
            </div>
          </div>

          <div className="qr_code_control_row">
            <label>{t("Corner Color")}</label>
            <div className="qr_color_picker">
              <input
                type="color"
                value={cornerColor}
                onChange={(e) => setCornerColor(e.target.value)}
              />
            </div>
          </div>

          <div className="qr_code_control_row">
            <label>{t("Dot Style")}</label>
            <select
              value={dotsType}
              onChange={(e) => setDotsType(e.target.value)}
            >
              {DOT_TYPE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(`qrCode.dotStyle.${option}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="qr_code_control_row">
            <label>{t("Corner Style")}</label>
            <select
              value={cornersSquareType}
              onChange={(e) => setCornersSquareType(e.target.value)}
            >
              {CORNER_SQUARE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(`qrCode.cornerStyle.${option}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="qr_code_actions">
          <button
            type="button"
            className="main_btn outline"
            onClick={() => handleDownload("png")}
          >
            {t("Download PNG")}
          </button>
          <button
            type="button"
            className="main_btn outline"
            onClick={() => handleDownload("svg")}
          >
            {t("Download SVG")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QRCodeCard;
